/* eslint-disable react/no-danger */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import {
  GridComponent,
  Inject,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Sort,
  Selection,
} from '@syncfusion/ej2-react-grids';
import Header from './Header';
import AMap from './AMap';
import profileImage from '../data/profile.png';
import armband from '../data/watchsketch.png';
import '../pages/Zackat.css';

const REFRESH_MS = 30000;
const GRID_FIELDS = ['employeeImage', 'name', 'timestamp', 'risk', 'organization', 'action'];
const PANEL_FIELDS = ['name', 'dob', 'age', 'organization', 'language', 'risk', 'hr', 'hrv', 'oxy', 'temp', 'hyd', 'rr'];
const LANGUAGES = {
  'en-US': 'English',
  'de-DE': 'German',
  'fr-FR': 'French',
  'fr-CA': 'French (Canadian)',
  'es-US': 'Spanish (US)',
  'es-ES': 'Spanish',
  'pt-BR': 'Portugese (Brazil)',
  'pt-PT': 'Portugese (Portugal)',
  'da-DK': 'Danish',
  'nl-NL': 'Dutch',
  'en-AU': 'Austrailian',
  'en-GB': 'English (British)',
  'en-IN': 'English (India)',
  'en-GB-WLS': 'English (Welsh)',
  'is-IS': 'Icelandic',
  'it-IT': 'Italian',
  'ja-JP': 'Japnense',
  'ko-KR': 'Korean',
  'nb-NO': 'Norweigan',
  'pl-PL': 'Polish',
  'ro-RO': 'Romanian',
  'ru-RU': 'Russian',
  'sv-SE': 'Swedish',
  'tr-TR': 'Turkish',
  'cy-GB': 'Welsh',
  'zh-CN': 'Chinese (Simplified)',
  'zh-TW': 'Chinese (Traditional)',
};

const statBox = {
  backgroundColor: '#F6821E7f',
  color: 'black',
  padding: '16px',
  textAlign: 'left',
  borderRadius: '8px',
  fontSize: '1.05rem',
  minWidth: 0,
};

const readingOf = (obj) => (obj && obj.reading != null && obj.reading !== '' ? String(obj.reading) : '');

const ageOf = (dob) => {
  if (!dob) return '';
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return String(age);
};

const languageOf = (code) => LANGUAGES[code] || code || '';

const riskNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : -1;
};

const latestActivity = (associate) => {
  let action = 'No Action';
  if (associate.resultField && associate.resultField.length > 0 && associate.resultField[0].charger === 'full') {
    action = 'Battery Full';
  }
  const addAlert = (label) => {
    action = action === 'No Action' ? label : `${action}, ${label.replace('Alert: ', '')}`;
  };
  if (!associate.hipaa || associate.hipaa.consent === true) {
    if (associate.temperature && associate.temperature.alert && associate.temperature.alert.length > 0) {
      action = 'Alert: CBT';
    } else if (associate.temperature) {
      const ageMinutes = (Date.now() - new Date(associate.temperature.timestamp).getTime()) / (60 * 1000);
      if (ageMinutes >= 0 && ageMinutes <= 15) {
        action = action === 'No Action' ? `CBT ${associate.temperature.reading}°F` : `${action}, CBT ${associate.temperature.reading}°F`;
      }
    }
    if (associate.heartrate && associate.heartrate.alert && associate.heartrate.alert.length > 0) {
      addAlert('Alert: HRM');
    } else if (associate.heartrate) {
      const ageMinutes = (Date.now() - new Date(associate.heartrate.timestamp).getTime()) / (60 * 1000);
      if (ageMinutes >= 0 && ageMinutes <= 15) {
        action = action === 'No Action' ? `HRM ${associate.heartrate.reading}bpm` : `${action}, HRM ${associate.heartrate.reading}bpm`;
      }
    }
    if (associate.hrv && associate.hrv.alert && associate.hrv.alert.length > 0) {
      addAlert('Alert: HRV');
    } else if (associate.hrv) {
      const ageMinutes = (Date.now() - new Date(associate.hrv.timestamp).getTime()) / (60 * 1000);
      if (ageMinutes >= 0 && ageMinutes <= 15) {
        action = action === 'No Action' ? `HRV ${associate.hrv.reading}ms` : `${action}, HRV ${associate.hrv.reading}ms`;
      }
    }
    if (associate.hydration && associate.hydration.alert && associate.hydration.alert.length > 0) {
      addAlert('Alert: HYM');
    } else if (associate.hydration) {
      const ageMinutes = (Date.now() - new Date(associate.hydration.timestamp).getTime()) / (60 * 1000);
      if (ageMinutes >= 0 && ageMinutes <= 15) {
        action = action === 'No Action' ? `HYM ${associate.hydration.reading}%` : `${action}, HYM ${associate.hydration.reading}%`;
      }
    }
    if (associate.oxygen && associate.oxygen.alert && associate.oxygen.alert.length > 0) {
      addAlert('Alert: OXM');
    } else if (associate.oxygen) {
      const ageMinutes = (Date.now() - new Date(associate.oxygen.timestamp).getTime()) / (60 * 1000);
      if (ageMinutes >= 0 && ageMinutes <= 15) {
        action = action === 'No Action' ? `OXM ${associate.oxygen.reading}%` : `${action}, OXM ${associate.oxygen.reading}%`;
      }
    }
    if (associate.respiratory && associate.respiratory.alert && associate.respiratory.alert.length > 0) {
      addAlert('Alert: RRM');
    } else if (associate.respiratory) {
      const ageMinutes = (Date.now() - new Date(associate.respiratory.timestamp).getTime()) / (60 * 1000);
      if (ageMinutes >= 0 && ageMinutes <= 15) {
        action = action === 'No Action' ? `RRM ${associate.respiratory.reading}b/m` : `${action}, RRM ${associate.respiratory.reading}b/m`;
      }
    }
  }
  if (associate.alarm && associate.alarm.alert && associate.alarm.alert.length > 0) {
    const ageMinutes = (Date.now() - new Date(associate.alarm.timestamp).getTime()) / (60 * 1000);
    if (ageMinutes >= 0 && ageMinutes <= 15) {
      action = action === 'No Action' ? `ALM ${associate.alarm.reading} ` : `${action}, ALM ${associate.alarm.reading} `;
    }
  }
  if (associate.resultField && associate.resultField.length > 0) {
    if (associate.resultField[0].charger && associate.resultField[0].charger === 'on') {
      action += ', On Charger';
    }
  }
  if (action === 'No Action' && associate.latlng && associate.latlng.timestamp) {
    const ld = new Date(associate.latlng.timestamp).getTime();
    const ageMinutes = (Date.now() - ld) / (60 * 1000);
    if (ageMinutes >= 0 && ageMinutes <= 8 * 60) {
      if (ageMinutes <= 60) {
        action = `Location ${Math.round(ageMinutes)} minutes ago`;
      } else {
        action = `Location ${Math.round(ageMinutes / 60)} hours ago`;
      }
    }
  }

  return action;
};

const snapshotOf = (row) => ({
  employeeImage: row.employeeImage || '',
  name: row.name || '',
  timestamp: row.timestamp || '',
  risk: String(riskNumber(row.risk)),
  organization: row.organization || '',
  action: row.action || '',
  dob: row.dob || '',
  age: ageOf(row.dob),
  language: languageOf(row.language),
  hr: readingOf(row.heartrate),
  hrv: readingOf(row.hrv),
  oxy: readingOf(row.oxygen),
  temp: readingOf(row.temperature),
  hyd: readingOf(row.hydration),
  rr: readingOf(row.respiratory),
});

const formatReport = (timestamp) => {
  if (!timestamp) return '';
  try {
    return format(parseISO(timestamp), 'MM-dd-yy p');
  } catch (error) {
    return '';
  }
};

const riskBands = (risk) => {
  const n = Number(risk);
  const bands = {
    hypo: 'transparent',
    base: 'transparent',
    moderate: 'transparent',
    elevated: 'transparent',
    severe: 'transparent',
  };
  if (!Number.isFinite(n)) return bands;
  if (n < 0) bands.hypo = 'cyan';
  else if (n < 25) bands.base = 'cyan';
  else if (n < 50) bands.moderate = 'cyan';
  else if (n < 80) bands.elevated = 'cyan';
  else bands.severe = 'cyan';
  return bands;
};

const changedStyle = (on) => (on ? { backgroundColor: '#F6821E' } : undefined);

function OrgAnalytics() {
  const navigate = useNavigate();
  const rawUser = localStorage.getItem('user');
  const rawOrg = localStorage.getItem('organization');
  const usr = rawUser ? JSON.parse(rawUser) : null;
  const homeOrg = rawOrg ? JSON.parse(rawOrg) : null;
  const { REACT_APP_API_ENDPOINT } = process.env;
  const grid = useRef(null);
  const prevRef = useRef(null);
  const selectedIdRef = useRef('');
  const [orgs, setOrgs] = useState([]);
  const [selOrganization, setSelOrganization] = useState(localStorage.getItem('analyticsOrganization') || (homeOrg && homeOrg.id) || '');
  const [associates, setAssociates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [inventoryCount, setInventoryCount] = useState(0);
  const [managerCount, setManagerCount] = useState(0);
  const [associateCount, setAssociateCount] = useState(0);
  const [numActive, setNumActive] = useState(0);
  const [statsChanged, setStatsChanged] = useState({});
  const [panelChanged, setPanelChanged] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!usr) {
      navigate('/login?landing=analytics');
    }
  }, [navigate]);

  const [trendHtml, setTrendHtml] = useState('<h1></h1>');
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  useEffect(() => {
    if (!selectedId) return;
    const fetchTrends = async (row) => {
      setTrendHtml('<h1>Loading trend data...</h1>');
      if (!row || !row._id) return;
      const userPrompt = "Show trends and comparisons of the associate's data.";
      const query = Object.entries(usr)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');
      const uri = `${REACT_APP_API_ENDPOINT}/faq?${query}`;
      const payload = { question: userPrompt, kbid: 'Predictive Analysis', patientid: row._id };
      const response = await fetch(uri, {
        method: 'PUT',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWTToken ${usr.token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const mydata = await response.json()
        .catch((err) => {
          console.error('Error parsing JSON:', err);
          return null;
        });
      const src = (mydata && (mydata.hr || mydata.avg_hr || mydata.tmp || mydata.avg_tmp))
        ? mydata
        : (mydata && mydata.answer && typeof mydata.answer === 'object' ? mydata.answer : mydata) || {};
      const mytrendHtml = (typeof src.answer === 'string' && src.answer)
      || (mydata && typeof mydata.answer === 'string' ? mydata.answer : '');
      setTrendHtml(mytrendHtml);
    };
    fetchTrends(selectedAssociate);
    console.log('Trend HTML: ', trendHtml);
  }, [selectedAssociate]);

  useEffect(() => {
    if (!usr) return undefined;
    let cancelled = false;
    const loadOrgs = async () => {
      const query = Object.entries(usr)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');
      const response = await fetch(`${REACT_APP_API_ENDPOINT}/organizations?${query}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWTToken ${usr.token}`,
        },
      }).then((res) => res.json()).catch(() => null);
      if (cancelled || !response || !response.organization) return;
      setOrgs(response.organization);
      setSelOrganization((current) => {
        if (current && response.organization.some((org) => org._id === current)) return current;
        const fallback = (homeOrg && homeOrg.id) || (response.organization[0] && response.organization[0]._id) || '';
        if (fallback) localStorage.setItem('analyticsOrganization', fallback);
        return fallback;
      });
    };
    loadOrgs();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!usr) return undefined;
    let cancelled = false;
    prevRef.current = null;

    const load = async (isRefresh) => {
      if (!selOrganization) {
        if (!cancelled) {
          setAssociates([]);
          setLoading(false);
        }
        return;
      }
      if (!isRefresh) setLoading(true);
      const queryUser = { ...usr, organizationid: selOrganization };
      const query = Object.entries(queryUser)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `JWTToken ${usr.token}`,
      };
      try {
        const [countData, associateData] = await Promise.all([
          fetch(`${REACT_APP_API_ENDPOINT}/associates?count=true&${query}&specific=true`, { method: 'GET', headers })
            .then((res) => res.json()),
          fetch(`${REACT_APP_API_ENDPOINT}/associates?${query}&limit=0&specific=true`, { method: 'GET', headers })
            .then((res) => res.json()),
        ]);
        if (cancelled) return;
        const stats = {
          inventory: Number(countData && countData.inventoryCount) || 0,
          managers: Number(countData && countData.managerCount) || 0,
          associates: Number(countData && countData.associateCount) || 0,
          active: 0,
        };
        const rows = ((associateData && associateData.associates) || []).map((associate) => ({
          ...associate,
          risk: riskNumber(associate.risk),
          action: latestActivity(associate),
        }));
        stats.active = rows.filter((row) => row.device && row.device.length > 0).length;
        rows.sort((a, b) => b.risk - a.risk);

        const prev = prevRef.current;
        const sameOrg = prev && prev.orgId === selOrganization;
        const nextStatsChanged = {
          inventory: Boolean(sameOrg && prev.stats.inventory !== stats.inventory),
          managers: Boolean(sameOrg && prev.stats.managers !== stats.managers),
          associates: Boolean(sameOrg && prev.stats.associates !== stats.associates),
          active: Boolean(sameOrg && prev.stats.active !== stats.active),
        };
        const nextSnap = {};
        rows.forEach((row) => {
          const snap = snapshotOf(row);
          nextSnap[row._id] = snap;
          row._changed = {};
          if (sameOrg && prev.rows[row._id]) {
            GRID_FIELDS.forEach((field) => {
              if (prev.rows[row._id][field] !== snap[field]) row._changed[field] = true;
            });
          }
        });
        const keepId = selectedIdRef.current;
        const nextId = (keepId && nextSnap[keepId]) ? keepId : ((rows[0] && rows[0]._id) || '');
        const nextPanelChanged = {};
        if (sameOrg && nextId && prev.rows[nextId]) {
          PANEL_FIELDS.forEach((field) => {
            if (prev.rows[nextId][field] !== nextSnap[nextId][field]) nextPanelChanged[field] = true;
          });
        }
        prevRef.current = { orgId: selOrganization, stats, rows: nextSnap };
        selectedIdRef.current = nextId;
        setInventoryCount(stats.inventory);
        setManagerCount(stats.managers);
        setAssociateCount(stats.associates);
        setNumActive(stats.active);
        setStatsChanged(nextStatsChanged);
        setPanelChanged(nextPanelChanged);
        setAssociates(rows);
        setSelectedId(nextId);
      } catch (error) {
        console.error(error);
      }
      if (!cancelled) setLoading(false);
    };

    load(false);
    const timer = setInterval(() => load(true), REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [selOrganization]);

  if (!usr) {
    return <p>Please login... </p>;
  }

  const selected = associates.find((row) => row._id === selectedId) || null;
  const orgTitle = (orgs.find((org) => org._id === selOrganization) || {}).title || '';
  const bands = selected ? riskBands(selected.risk) : riskBands(null);
  const showVitals = selected && ((selected.hipaa && selected.hipaa.consent === true) || !selected.hipaa);
  const mark = (key, value) => <span style={changedStyle(panelChanged[key])}>{value}</span>;

  const imageTemplate = (row) => (
    <img className="rounded-full w-10 h-10" src={row.employeeImage || profileImage} alt="" />
  );
  const nameTemplate = (row) => (
    <Link
      to="/associatelayout"
      style={{ color: 'blue', textDecoration: 'underline' }}
      onClick={() => { localStorage.setItem('currentAssociate', row.name); }}
    >
      {row.name}
    </Link>
  );
  const reportTemplate = (row) => <span>{formatReport(row.timestamp)}</span>;
  const onQueryCellInfo = (args) => {
    if (!args || !args.cell || !args.data || !args.column) return;
    const changed = args.data._changed || {};
    args.cell.classList.toggle('org-analytics-changed', Boolean(changed[args.column.field]));
  };
  const onDataBound = () => {
    const gridObj = grid.current;
    if (!gridObj || !selectedId || !gridObj.getCurrentViewRecords) return;
    const view = gridObj.getCurrentViewRecords();
    const viewIndex = view.findIndex((row) => row._id === selectedId);
    const selectedRows = gridObj.getSelectedRecords ? gridObj.getSelectedRecords() : [];
    if (viewIndex >= 0 && (!selectedRows.length || selectedRows[0]._id !== selectedId)) {
      gridObj.selectRow(viewIndex);
    }
  };
  const onActionBegin = (args) => {
    if (args.requestType === 'sorting' && !(args.columnName === 'risk' && args.direction === 'Descending')) {
      args.cancel = true;
    }
  };

  return (
    <div className="m-2 md:m-1 mt-2 p-1 md:p-4 bg-white rounded-3xl zackat-divalh max-w-full overflow-x-hidden">
      <Header category="Dashboards" title={orgTitle ? `Analytics: ${orgTitle}` : 'Analytics'} />
      <div style={{ width: '100%', marginBottom: 12 }}>
        <DropDownListComponent
          id="org-analytics-org"
          floatLabelType="Auto"
          cssClass="e-outline"
          dataSource={orgs}
          fields={{ value: '_id', text: 'title' }}
          placeholder="Select Organization"
          sortOrder="Ascending"
          value={selOrganization}
          width="100%"
          change={(event) => {
            const value = (event && event.value) || '';
            selectedIdRef.current = '';
            setSelectedId('');
            setSelOrganization(value);
            localStorage.setItem('analyticsOrganization', value);
          }}
        />
      </div>
      <div className="org-analytics-stats">
        <div style={{ ...statBox, ...changedStyle(statsChanged.inventory) }}>
          <img src={armband} width={35} height={30} alt="" />
          {' '}
          {inventoryCount}
          {' '}
          (Number of W<sup>3</sup> Devices)
        </div>
        <div style={{ ...statBox, ...changedStyle(statsChanged.managers) }}>
          👫
          {' '}
          {managerCount}
          {' '}
          (Number of Active W<sup>3</sup> Managers)
        </div>
        <div style={{ ...statBox, ...changedStyle(statsChanged.active) }}>
          🕺
          {' '}
          {numActive}
          {' '}
          (Number of Associates assigned to W<sup>3</sup> Devices)
        </div>
        <div style={{ ...statBox, ...changedStyle(statsChanged.associates) }}>
          👥
          {' '}
          {associateCount}
          {' '}
          (Number of W<sup>3</sup> Associates)
        </div>
      </div>
      {loading && <p>Loading... </p>}
      {!loading && selOrganization && (
        <div className="org-analytics-body">
          <div className="org-analytics-side">
            <div className="e-panel-container zackat-divalh" style={{ backgroundColor: selected && selected.bp === 'duress' ? 'red' : 'transparent', padding: '12px' }}>
              <span style={{ display: 'block', textAlign: 'center' }}>Associate</span>
              {selected ? (
                <div className="m-2">
                  {selected.bp === 'duress' && (
                    <div className="mt-2 mb-2 flash-animation" style={{ backgroundColor: 'white', textAlign: 'center', fontSize: '1.5rem' }}>
                      DURESS
                    </div>
                  )}
                  <div>Name: {mark('name', selected.name || '')}</div>
                  <div>DOB: {mark('dob', selected.dob || '')} Age: {mark('age', ageOf(selected.dob))}</div>
                  <div>Organization: {mark('organization', selected.organization || '')}</div>
                  <div>Language: {mark('language', languageOf(selected.language))}</div>
                </div>
              ) : <p>Select an associate</p>}
            </div>
            <div className="e-panel-container zackat-divalh" style={{ overflowY: 'auto', width: '100%', marginTop: 12 }}>
              <span style={{ display: 'block', textAlign: 'center' }}> BIOMETRICS</span>
              <table className="biotable1 biotable-fill">
                <tbody>
                  <tr>
                    <th style={{ backgroundColor: bands.hypo, padding: '0.5rem', width: '20%', textAlign: 'center' }}><font color="blue">HYPO</font></th>
                    <th style={{ backgroundColor: bands.base, width: '20%', padding: '0.5rem', textAlign: 'center' }}><font color="green">BASELINE</font></th>
                    <th style={{ backgroundColor: bands.moderate, width: '20%', padding: '0.5rem', textAlign: 'center' }}><font color="yellow">MODERATE</font></th>
                    <th style={{ backgroundColor: bands.elevated, width: '20%', padding: '0.5rem', textAlign: 'center' }}><font color="orange">ELEVATED</font></th>
                    <th style={{ backgroundColor: bands.severe, width: '20%', padding: '0.5rem', textAlign: 'center' }}><font color="red">SEVERE</font></th>
                  </tr>
                </tbody>
              </table>
              {selected && showVitals && (
                <table className="biotable1 biotable-fill">
                  <tbody>
                    <tr>
                      <th colSpan={2} style={{ backgroundColor: 'white' }}>Risk Level {mark('risk', selected.risk)}</th>
                    </tr>
                    <tr>
                      <td className={selected.heartrate && selected.heartrate.alert && selected.heartrate.alert.length > 0 ? 'biometricsred flash-animation' : 'biometricsgrn'}>
                        <sup className="supsm">hr</sup>
                        {' '}
                        💖
                        {mark('hr', readingOf(selected.heartrate))}
                        <sub className="supsm">bpm</sub>
                      </td>
                      <td className={selected.hrv && selected.hrv.alert && selected.hrv.alert.length > 0 ? 'biometricsred flash-animation' : 'biometricsltgrn'}>
                        <sup className="supsm">hrv</sup>
                        {' '}
                        💗
                        {mark('hrv', readingOf(selected.hrv))}
                        <sub className="supsm">ms</sub>
                      </td>
                    </tr>
                    <tr>
                      <td className={selected.oxygen && selected.oxygen.alert && selected.oxygen.alert.length > 0 ? 'biometricsred flash-animation' : 'biometricsorn'}>
                        <sup className="supsm">oxy</sup>
                        {' '}
                        <span className="biometricsltblue">O<sub>2</sub></span>
                        {mark('oxy', readingOf(selected.oxygen))}
                        <sub className="supsm">%</sub>
                      </td>
                      <td className={selected.temperature && selected.temperature.alert && selected.temperature.alert.length > 0 ? 'biometricsred flash-animation' : 'biometricsylw'}>
                        <sup className="supsm">temp</sup>
                        {' '}
                        <span className="rotate-char">🌡</span>
                        {mark('temp', readingOf(selected.temperature))}
                        <sub className="supsm">°F</sub>
                      </td>
                    </tr>
                    <tr>
                      <td className={selected.hydration && selected.hydration.alert && selected.hydration.alert.length > 0 ? 'biometricsred flash-animation' : 'biometricsblue'}>
                        <sup className="supsm">hyd</sup>
                        {' '}
                        💦
                        {mark('hyd', readingOf(selected.hydration))}
                        <sub className="supsm">%</sub>
                      </td>
                      <td className={selected.respiratory && selected.respiratory.alert && selected.respiratory.alert.length > 0 ? 'biometricsred flash-animation' : 'biometricsblue'}>
                        <sup className="supsm">rr</sup>
                        {' '}
                        🫁
                        {mark('rr', readingOf(selected.respiratory))}
                        <sub className="supsm">b/m</sub>
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
              {selected && !showVitals && (
                <div style={{ textAlign: 'center' }}>███</div>
              )}
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ height: '45px', textAlign: 'center' }}><h2>LIVE WORKER MAP</h2></div>
              <AMap key={selOrganization} active org={selOrganization} frequency="week" refreshMs={REFRESH_MS} />
            </div>
          </div>
          <div className="org-analytics-grid">
            <GridComponent
              dataSource={associates}
              allowPaging
              pageSettings={{ pageSize: 12 }}
              allowSorting
              allowTextWrap
              sortSettings={{ columns: [{ field: 'risk', direction: 'Descending' }] }}
              width="100%"
              enableAltRow
              queryCellInfo={onQueryCellInfo}
              dataBound={onDataBound}
              actionBegin={onActionBegin}
              rowSelected={(args) => {
                if (args && args.data && args.data._id && args.data._id !== selectedIdRef.current) {
                  selectedIdRef.current = args.data._id;
                  setPanelChanged({});
                  setSelectedId(args.data._id);
                  setSelectedAssociate(args.data);
                }
              }}
              ref={grid}
            >
              <ColumnsDirective>
                <ColumnDirective field="_id" headerText="ID" isPrimaryKey visible={false} />
                <ColumnDirective field="employeeImage" headerText="Image" width="90" allowSorting={false} template={imageTemplate} textAlign="Center" />
                <ColumnDirective field="name" headerText="Name" width="140" template={nameTemplate} />
                <ColumnDirective field="timestamp" headerText="Last Report" width="140" template={reportTemplate} />
                <ColumnDirective field="risk" headerText="Risk" width="90" textAlign="Center" type="number" />
                <ColumnDirective field="environment" headerText="Environment" width="90" textAlign="Center" type="number" />
                {/* <ColumnDirective field="organization" headerText="Organization" width="150" /> */}
                <ColumnDirective field="action" headerText="Latest Activity" width="180" />
              </ColumnsDirective>
              <Inject services={[Page, Sort, Selection]} />
            </GridComponent>
            <div dangerouslySetInnerHTML={{ __html: (trendHtml && trendHtml.length > 0 && selectedAssociate && selectedAssociate._id) ? `<h1>Associate: ${selectedAssociate && selectedAssociate.name && selectedAssociate.name.length > 0 ? selectedAssociate.name : ''} Predictive Trend Analysis</h1> ${trendHtml}` : '<h1></h1>' }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default OrgAnalytics;
